import React, { memo, useId } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

interface FormFieldProps {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  icon: React.ElementType;
  options?: { value: string; label: string; }[] | null;
  placeholder?: string;
  min?: number;
  step?: number;
  disabled?: boolean;
  hint?: string;
  className?: string;
  validate?: (value: unknown) => boolean | string;
}

// Memoized form field component to prevent unnecessary re-renders
const FormField = memo(({ 
  name, 
  label, 
  type = 'text',
  required = false,
  icon: Icon,
  options = null,
  placeholder = '',
  min,
  step,
  disabled = false,
  hint = '',
  className = '',
  validate
}: FormFieldProps) => {
  const { control, formState: { errors = {} } } = useFormContext();
  const uniqueId = useId();
  const inputId = `field-${name}-${uniqueId}`;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const describedBy = [hint ? hintId : null, (errors as any)[name] ? errorId : null].filter(Boolean).join(' ') || undefined;
  
  return (
    <div className={className}>
      <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-1">
        <span className="flex items-center">
          <Icon className="h-4 w-4 mr-1.5 text-gray-400" />
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </span>
      </label>
      <Controller
        name={name}
        control={control}
        rules={{ required: required ? `${label} è obbligatorio` : false, validate }}
        render={({ field }) => {
          // Create a local reference to avoid losing field props
          const inputProps = {
            ...field,
            id: inputId,
            'aria-describedby': describedBy,
            'aria-required': required || undefined,
            'aria-invalid': (errors as any)[name] ? true : undefined,
            value: field.value ?? '',
            onChange: (e: any) => {
              const val = e?.target?.value !== undefined ? e.target.value : e;
              field.onChange(val);
            },
            onBlur: field.onBlur,
            min,
            step,
            disabled,
            placeholder,
          };

          return options ? (
            <select
              {...inputProps}
              className={`w-full border ${
                (errors as any)[name] ? 'border-red-500' : 'border-gray-300'
              } rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent ${
                disabled ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
            >
              <option value="">{placeholder || `Seleziona ${label.toLowerCase()}`}</option>
              {options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              {...inputProps}
              type={type}
              className={`w-full border ${
                (errors as any)[name] ? 'border-red-500' : 'border-gray-300'
              } rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent ${
                disabled ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
            />
          );
        }}
      />
      {hint && (
        <p id={hintId} className="mt-1 text-sm text-gray-500">{hint}</p>
      )}
      {(errors as any)[name] && (
        <p id={errorId} role="alert" className="mt-1 text-sm text-red-500">{(errors as any)[name]?.message}</p>
      )}
    </div>
  );
});

export default FormField;